// src/pages/settings/settingsPage.js
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { Button, Card, Modal, Segmented } from "antd";
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import useNotification from "../../hooks/useNotification";
import Layout from "../../components/layout/layout";
import { useTheme } from "../../context/themeContext";
import GeneralSettings from "./generalSettings";
import ProfileSettings from "./profileSettings";
import SecuritySettings from "./securitySettings";
import HelpSettings from "./helpSettings";
import "./settingsPage.css";

// ─── Local-only fields ────────────────────────────────────────────────────────
// Fields listed here are stored in localStorage only and never sent to the
// backend. Add new local-only fields here as settings grow — handleSave will
// strip them automatically before PATCHing each section.
const LOCAL_ONLY_FIELDS = {
  general: ["theme"],
  profile: [],
  security: []
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Strip local-only fields from a section's data before sending to the backend.
const stripLocalFields = (section, data) => {
  const localFields = LOCAL_ONLY_FIELDS[section] || [];
  if (localFields.length === 0) return { ...data };
  const stripped = { ...data };
  localFields.forEach((f) => delete stripped[f]);
  return stripped;
};

// Sanitise profile before sending — churchLogo is always a plain string or null.
const sanitiseProfile = (profile) => ({
  ...profile,
  churchLogo: profile.churchLogo || null
});

// Read theme from localStorage, defaulting to "light".
const getStoredTheme = () => localStorage.getItem("theme") || "light";

// Read and parse profile from localStorage, returning null if missing or invalid.
const getStoredProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("settings_profile") || "null");
  } catch {
    return null;
  }
};

// ─── Default section shapes ───────────────────────────────────────────────────

const DEFAULT_GENERAL = {
  theme: "light"
  // Future general settings go here
};

const DEFAULT_PROFILE = {
  churchCode: "",
  churchName: "",
  churchLogo: null,
  description: "",
  website: "",
  socialMedia: [""],
  address: "",
  addressLink: "",
  phoneNumbers: [""],
  emails: [""],
  operatingHours: [""]
};

const DEFAULT_SECURITY = {
  // Future security settings go here
};

// ─── Initial state ────────────────────────────────────────────────────────────

const buildDefaultSettings = () => ({
  general: { ...DEFAULT_GENERAL },
  profile: { ...DEFAULT_PROFILE },
  security: { ...DEFAULT_SECURITY }
});

const initialState = {
  selectedOption: "General",

  showSaveConfirmModal: false,
  showClearCacheConfirmModal: false,

  // Profile loading is tracked separately since it's the only async section
  isLoadingProfile: false,
  isSavingSettings: false,

  settingsData: buildDefaultSettings(),
  settingsSnapshot: buildDefaultSettings(),

  dirtyMap: {
    general: false,
    profile: false,
    security: false
  }
};

// ─── Reducer ──────────────────────────────────────────────────────────────────

const settingsReducer = (state, action) => {
  switch (action.type) {
    case "setSelectedOption":
      return { ...state, selectedOption: action.payload };

    case "setSaveConfirmModal":
      return { ...state, showSaveConfirmModal: action.payload };

    case "setClearCacheConfirmModal":
      return { ...state, showClearCacheConfirmModal: action.payload };

    case "setLoadingProfile":
      return { ...state, isLoadingProfile: action.payload };

    case "setSavingSettings":
      return { ...state, isSavingSettings: action.payload };

    // Initialise a section's data from a non-user source (localStorage, defaults).
    // Writes to both settingsData and settingsSnapshot atomically so the section
    // is never considered dirty — dirtyMap is intentionally left untouched.
    case "initSection": {
      const merged = {
        ...state.settingsData[action.section],
        ...action.payload
      };
      return {
        ...state,
        settingsData: {
          ...state.settingsData,
          [action.section]: merged
        },
        settingsSnapshot: {
          ...state.settingsSnapshot,
          [action.section]: merged
        }
        // dirtyMap deliberately not touched — init writes are not user edits
      };
    }

    case "saveSnapshot":
      return { ...state, settingsSnapshot: { ...state.settingsData } };

    case "restoreSnapshot":
      return {
        ...state,
        settingsData: { ...state.settingsSnapshot },
        dirtyMap: { general: false, profile: false, security: false }
      };

    case "updateField":
      return {
        ...state,
        settingsData: {
          ...state.settingsData,
          [action.section]: {
            ...state.settingsData[action.section],
            [action.field]: action.value
          }
        },
        dirtyMap: { ...state.dirtyMap, [action.section]: true }
      };

    case "updateSection":
      return {
        ...state,
        settingsData: {
          ...state.settingsData,
          [action.section]: {
            ...state.settingsData[action.section],
            ...action.data
          }
        },
        dirtyMap: { ...state.dirtyMap, [action.section]: true }
      };

    case "clearDirty": {
      const sections = action.sections || Object.keys(state.dirtyMap);
      const updated = { ...state.dirtyMap };
      sections.forEach((s) => {
        updated[s] = false;
      });
      return { ...state, dirtyMap: updated };
    }

    default:
      return state;
  }
};

// ─── Main component ───────────────────────────────────────────────────────────
function SettingsAndPreferences() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(settingsReducer, initialState);
  const { setTheme } = useTheme();

  const {
    selectedOption,
    showSaveConfirmModal,
    showClearCacheConfirmModal,
    isLoadingProfile,
    isSavingSettings,
    settingsData,
    settingsSnapshot,
    dirtyMap
  } = state;

  const hasUnsavedChanges = useMemo(() => Object.values(dirtyMap).some(Boolean), [dirtyMap]);

  const makeFieldChanger = useCallback((section) => (field, value) => dispatch({ type: "updateField", section, field, value }), []);

  // ── Load General & Security (synchronous, localStorage only) ─────────────
  // Uses "initSection" so the write is treated as initialisation, not a user
  // edit — dirtyMap stays false and no dot appears on the Save button.
  const loadLocalSettings = useCallback(() => {
    const storedTheme = getStoredTheme();

    dispatch({
      type: "initSection",
      section: "general",
      payload: { theme: storedTheme }
    });

    // Apply theme immediately from localStorage
    setTheme(storedTheme);
  }, [setTheme]);

  // ── Load Profile (async, API only) ───────────────────────────────────────
  // Completely isolated from General and Security.
  // Failures here only affect the Profile section — other sections are unaffected.
  // Also uses "initSection" so a successful or fallback load never marks dirty.
  const loadProfile = useCallback(async () => {
    dispatch({ type: "setLoadingProfile", payload: true });

    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.SETTINGS, {
        headers: { Authorization: `Bearer ${token}` }
      });

      dispatch({
        type: "initSection",
        section: "profile",
        payload: { ...DEFAULT_PROFILE, ...(response.data.profile || {}) }
      });
    } catch (error) {
      console.error("Error loading profile settings:", error);

      const localProfile = getStoredProfile();

      dispatch({
        type: "initSection",
        section: "profile",
        payload: { ...DEFAULT_PROFILE, ...(localProfile || {}) }
      });

      if (error.response?.status === 404) {
        // Silently use defaults — expected for a new admin
      } else if (localProfile) {
        showNotification("Could not reach server. Showing locally saved profile.", "warning", "Offline Mode");
      } else {
        showNotification("Failed to load profile settings. Showing defaults.", "warning", "Load Warning");
      }
    } finally {
      dispatch({ type: "setLoadingProfile", payload: false });
    }
  }, [showNotification]);

  // ── Save settings ─────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    dispatch({ type: "setSaveConfirmModal", payload: false });

    if (!hasUnsavedChanges) {
      showNotification("No changes to save.", "info", "Nothing Changed");
      return;
    }

    dispatch({ type: "setSavingSettings", payload: true });

    const dirtySections = Object.keys(dirtyMap).filter((s) => dirtyMap[s]);
    let apiSucceeded = false;

    try {
      const token = await getAuthToken();

      if (dirtyMap.profile) {
        const backendData = stripLocalFields("profile", sanitiseProfile(settingsData.profile));

        await axios.patch(API_ENDPOINTS.SETTINGS_PROFILE, backendData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      apiSucceeded = true;
    } catch (error) {
      console.error("Error saving settings:", error);

      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;

      if (status === 401) {
        showNotification("Session expired. Please log in again.", "error", "Auth Error");
      } else if (status === 403) {
        showNotification("You don't have permission to update settings.", "error", "Access Denied");
      } else if (status === 400 && serverMessage) {
        showNotification(serverMessage, "error", "Validation Error");
      } else {
        showNotification("Server unreachable. Changes saved locally.", "warning", "Saved Locally");
      }
    }

    // ── Persist local-only fields ─────────────────────────────────────────
    // Theme stored under its own key (used by ThemeContext globally)
    localStorage.setItem("theme", settingsData.general.theme);

    // Profile cached as offline fallback
    if (dirtySections.includes("profile")) {
      localStorage.setItem("settings_profile", JSON.stringify(sanitiseProfile(settingsData.profile)));
    }

    // Apply theme only on Save, not on dropdown change
    setTheme(settingsData.general.theme);

    dispatch({ type: "saveSnapshot" });
    dispatch({ type: "clearDirty", sections: dirtySections });
    dispatch({ type: "setSavingSettings", payload: false });

    if (apiSucceeded) {
      showNotification("Your settings have been updated successfully.", "success", "Settings Saved");
    }
  }, [hasUnsavedChanges, settingsData, dirtyMap, showNotification, setTheme]);

  // ── Discard changes ───────────────────────────────────────────────────────
  const handleDiscard = useCallback(() => {
    dispatch({ type: "restoreSnapshot" });
    // Revert theme to snapshot value in case the admin changed the dropdown
    setTheme(settingsSnapshot.general.theme);
    showNotification("All changes have been discarded.", "info", "Changes Discarded");
  }, [showNotification, setTheme, settingsSnapshot]);

  // ── Clear cache ───────────────────────────────────────────────────────────
  const handleClearCache = useCallback(async () => {
    dispatch({ type: "setClearCacheConfirmModal", payload: false });
    try {
      localStorage.clear();
      localStorage.setItem("theme", "light");
      sessionStorage.clear();
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
      showNotification("Site cache cleared. Reloading...", "success", "Cache Cleared");
      setTimeout(() => window.location.reload(true), 1000);
    } catch {
      showNotification("Failed to clear cache.", "error", "Cache Error");
    }
  }, [showNotification]);

  // ── Mount ─────────────────────────────────────────────────────────────────
  // General and Security load synchronously first — no loading state needed.
  // Profile loads asynchronously — has its own loading indicator.
  useEffect(() => {
    loadLocalSettings();
    loadProfile();
  }, [loadLocalSettings, loadProfile]);

  // ── Render active sub-page ────────────────────────────────────────────────
  const renderSubPage = useCallback(() => {
    switch (selectedOption) {
      case "General":
        return <GeneralSettings data={settingsData.general} onFieldChange={makeFieldChanger("general")} />;
      case "Profile":
        return (
          <ProfileSettings data={settingsData.profile} onFieldChange={makeFieldChanger("profile")} showNotification={showNotification} />
        );
      case "Security":
        return <SecuritySettings onClearCacheClick={() => dispatch({ type: "setClearCacheConfirmModal", payload: true })} />;
      case "Help":
        return <HelpSettings />;
      default:
        return <GeneralSettings data={settingsData.general} onFieldChange={makeFieldChanger("general")} />;
    }
  }, [selectedOption, settingsData, makeFieldChanger, showNotification]);

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Layout
      navTitle="Settings"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="stg-page-wrapper">
        <Segmented
          className="stg-option-bar"
          options={["General", "Profile", "Security", "Help"]}
          value={selectedOption}
          onChange={(value) => dispatch({ type: "setSelectedOption", payload: value })}
        />

        <Card className="stg-content-area" title={`${selectedOption} Settings`} loading={selectedOption === "Profile" && isLoadingProfile}>
          {renderSubPage()}

          <div className="stg-btns">
            <Button className="stg-cancel-btn" size="large" onClick={handleDiscard} disabled={isSavingSettings || !hasUnsavedChanges}>
              Discard Changes
            </Button>
            <Button
              className="stg-save-btn"
              size="large"
              onClick={() => dispatch({ type: "setSaveConfirmModal", payload: true })}
              loading={isSavingSettings}
              disabled={!hasUnsavedChanges}
              type={hasUnsavedChanges ? "primary" : "default"}
            >
              {hasUnsavedChanges ? "Update Changes ●" : "Update Changes"}
            </Button>
          </div>
        </Card>
      </div>

      {/* Save confirmation modal */}
      <Modal
        title="Confirm Settings Update"
        open={showSaveConfirmModal}
        onCancel={() => dispatch({ type: "setSaveConfirmModal", payload: false })}
        footer={[
          <Button className="usl-modal-cancel-btn" key="cancel" onClick={() => dispatch({ type: "setSaveConfirmModal", payload: false })}>
            Cancel
          </Button>,
          <Button className="usl-modal-confirm-btn" key="confirm" loading={isSavingSettings} onClick={handleSave}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>Are you sure you want to save these changes to your settings?</p>
      </Modal>

      {/* Clear cache confirmation modal */}
      <Modal
        title="Clear Site Cache"
        open={showClearCacheConfirmModal}
        onCancel={() => dispatch({ type: "setClearCacheConfirmModal", payload: false })}
        footer={[
          <Button
            className="usl-modal-cancel-btn"
            key="cancel"
            onClick={() => dispatch({ type: "setClearCacheConfirmModal", payload: false })}
          >
            Cancel
          </Button>,
          <Button className="usl-modal-delete-btn" key="confirm" onClick={handleClearCache}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>Are you sure you want to clear the site cache? The page will reload automatically after clearing.</p>
      </Modal>
    </Layout>
  );
}

export default SettingsAndPreferences;
